import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, ShieldCheck, FileText, AlertCircle, KeyRound } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const { login, register, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Bem-vindo de volta!");
      } else {
        await register(name, email, password);
        toast.success("Conta criada com sucesso!");
      }
      navigate("/");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Left visual panel */}
      <div className="hidden lg:flex relative overflow-hidden bg-brand-950 text-white">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/37355220/pexels-photo-37355220.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-950 via-brand-900/80 to-brand-800/40" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <BrandLogo size={40} rounded="rounded-md" />
            <span className="font-outfit font-semibold text-lg tracking-tight">Colenghi Transporte & Turismo</span>
          </div>

          <div className="space-y-8">
            <div>
              <h1 className="text-4xl xl:text-5xl font-outfit font-semibold tracking-tight leading-tight">
                Toda a documentação dos seus colaboradores em um só lugar.
              </h1>
              <p className="mt-4 text-slate-300 max-w-md">
                Contratos, notas fiscais, exames médicos e cursos com controle automático de vencimentos.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 max-w-md">
              {[
                { icon: Users, title: "Colaboradores", desc: "Cadastro completo com dados pessoais." },
                { icon: FileText, title: "Documentos em PDF", desc: "Upload seguro e organizado por categoria." },
                { icon: AlertCircle, title: "Alertas de Vencimento", desc: "Saiba o que precisa ser renovado." },
              ].map((f) => (
                <div key={f.title} className="flex items-start gap-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-md p-3">
                    <f.icon className="w-5 h-5 mt-0.5 text-accent2-300" strokeWidth={2} />
                  <div>
                    <div className="font-medium text-sm">{f.title}</div>
                    <div className="text-xs text-slate-300">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-slate-400">© {new Date().getFullYear()} Colenghi Transporte & Turismo</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md animate-fade-in-up">
          <div className="mb-8">
            <h2 className="text-3xl font-outfit font-semibold tracking-tight">
              {mode === "login" ? "Entrar na plataforma" : "Criar nova conta"}
            </h2>
            <p className="text-sm text-slate-500 mt-2">
              {mode === "login"
                ? "Acesse o painel para gerenciar seus colaboradores."
                : "Crie sua conta para começar a organizar."}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name">Nome completo</Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Seu nome"
                  autoComplete="off"
                  required
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@empresa.com"
                autoComplete="username"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Digite sua senha"
                autoComplete="current-password"
                required
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-11 bg-brand-900 hover:bg-brand-800 text-white"
            >
              {loading ? "Aguarde..." : mode === "login" ? "Entrar" : "Criar conta"}
            </Button>

            {mode === "login" && (
              <button
                type="button"
                onClick={() => { setForgotOpen(true); setForgotEmail(email); setForgotSent(false); }}
                data-testid="forgot-password-link"
                className="text-xs text-slate-500 hover:text-brand-900 hover:underline w-full text-center"
              >
                Esqueci minha senha
              </button>
            )}
          </form>

          <div className="mt-6 text-center text-sm text-slate-500">
            Apenas administradores criam contas. Solicite acesso ao seu administrador.
          </div>
        </div>
      </div>

      {/* Forgot password dialog */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="max-w-md" data-testid="forgot-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <KeyRound className="w-5 h-5" /> Esqueci minha senha
            </DialogTitle>
            <DialogDescription>
              Informe seu e-mail cadastrado e enviaremos um link para você criar uma nova senha.
            </DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <div className="py-6 text-center" data-testid="forgot-sent">
              <p className="text-sm">Se houver uma conta com este e-mail, você receberá um link de redefinição em instantes.</p>
              <p className="text-xs text-slate-500 mt-2">Verifique também sua caixa de spam. O link expira em 1 hora.</p>
            </div>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setForgotLoading(true);
                try {
                  await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
                  setForgotSent(true);
                  toast.success("E-mail enviado");
                } catch (err) {
                  toast.error("Falha ao enviar. Tente novamente.");
                } finally {
                  setForgotLoading(false);
                }
              }}
              className="space-y-3"
            >
              <div className="space-y-1.5">
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  placeholder="voce@empresa.com"
                  data-testid="forgot-email-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setForgotOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={forgotLoading} data-testid="forgot-submit" className="bg-brand-900 hover:bg-brand-800 text-white">
                  {forgotLoading ? "Enviando..." : "Enviar link"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
